import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { NotificationSettings, InsertNotificationSettings } from "@shared/schema";

interface UseNotificationSettingsProps {
  documentId: string;
  userId: string | null;
}

interface NotificationSettingsState {
  settings: NotificationSettings | null;
  isLoading: boolean;
  updateSettings: (settings: Partial<InsertNotificationSettings>) => Promise<void>;
  isUpdating: boolean;
}

export function useNotificationSettings({ 
  documentId, 
  userId 
}: UseNotificationSettingsProps): NotificationSettingsState {
  const queryClient = useQueryClient();

  // Query for notification settings
  const { data: settings = null, isLoading } = useQuery<NotificationSettings>({
    queryKey: ['/api/documents', documentId, 'notifications/settings', userId],
    enabled: !!userId, // Only fetch when userId exists
    staleTime: Infinity,
  });

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: async (settingsUpdate: Partial<InsertNotificationSettings>): Promise<NotificationSettings> => {
      if (!userId) throw new Error('User ID is required');
      
      const response = await apiRequest(
        'PUT', 
        `/api/documents/${documentId}/notifications/settings/${userId}`, 
        settingsUpdate
      );
      return response.json();
    },
    onSuccess: (updatedSettings: NotificationSettings) => {
      // Update cache
      queryClient.setQueryData(
        ['/api/documents', documentId, 'notifications/settings', userId],
        updatedSettings
      );
      
      // Update localStorage cache
      if (userId) {
        const cacheKey = `notification-settings-${documentId}-${userId}`;
        localStorage.setItem(cacheKey, JSON.stringify(updatedSettings));
      }
    },
  });

  // Load from localStorage on mount if available
  useEffect(() => {
    if (userId && !settings && !isLoading) {
      const cacheKey = `notification-settings-${documentId}-${userId}`;
      const cached = localStorage.getItem(cacheKey);
      
      if (cached) {
        try {
          const cachedSettings = JSON.parse(cached);
          queryClient.setQueryData(
            ['/api/documents', documentId, 'notifications/settings', userId],
            cachedSettings
          );
        } catch (error) {
          console.warn('Failed to parse cached notification settings:', error);
          localStorage.removeItem(cacheKey);
        }
      }
    }
  }, [userId, documentId, settings, isLoading, queryClient]);

  const updateSettings = async (settingsUpdate: Partial<InsertNotificationSettings>) => {
    await updateSettingsMutation.mutateAsync(settingsUpdate);
  };

  return {
    settings,
    isLoading,
    updateSettings,
    isUpdating: updateSettingsMutation.isPending,
  };
}